import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-white text-primary hover:bg-primary/10",
                destructive: "bg-white text-destructive border border-destructive hover:bg-destructive hover:text-white",
                outline: "border border-input bg-white hover:bg-accent hover:text-accent-foreground",
                secondary: "bg-white text-secondary-foreground border border-secondary hover:bg-secondary/10",
                ghost: "bg-white hover:bg-accent hover:text-accent-foreground",
                link: "bg-white text-primary underline-offset-4 hover:underline",
                save: "bg-white border border-green-500 text-green-500 hover:bg-green-500 hover:text-white",
                view: "bg-white border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white",
                delete: "bg-white border border-red-500 text-red-500 hover:bg-red-500 hover:text-white",
                cancel: "bg-white border border-red-500 text-red-500 hover:bg-red-500 hover:text-white",
                edit: "bg-white border border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-white",
                reset: "bg-white border border-gray-500 text-gray-500 hover:bg-gray-500 hover:text-white",
                search: "bg-white border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white",
                create: "bg-white border border-green-500 text-green-500 hover:bg-green-500 hover:text-white",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10"
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
